import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Typography, Box, TextField, InputAdornment,
  Stack, Pagination, Divider, Button, Modal, IconButton, Snackbar, Alert
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL;
const RESERVATIONS_PER_PAGE = 10;

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: '#1e1e1e',
  color: '#fff',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const UserReservationsTable = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const [toastOpen, setToastOpen] = useState(false);

  // Fetch data
  useEffect(() => {
    fetch(`${API_URL}/api/user-reservations-full/`)
      .then((res) => res.json())
      .then((data) => {
        setReservations(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching reservations:', error);
        setLoading(false);
      });
  }, []);

  // Track loading time
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingSeconds(0);
      timer = setInterval(() => setLoadingSeconds(prev => prev + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [loading]);

  // Sorting logic
  const handleSort = (key) => {
    let direction = 'asc';

    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
  };

  const sortedReservations = [...reservations].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const v1 = a[sortConfig.key] || '';
    const v2 = b[sortConfig.key] || '';

    if (sortConfig.direction === 'asc') {
      return v1.toString().localeCompare(v2.toString());
    } else {
      return v2.toString().localeCompare(v1.toString());
    }
  });

  // Filtering
  const filteredReservations = sortedReservations.filter((res) => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;

    return (
      res.post_title?.toLowerCase().includes(q) ||
      res.user_name?.toLowerCase().includes(q) ||
      res.user_surname?.toLowerCase().includes(q) ||
      res.user_email?.toLowerCase().includes(q)
    );
  });

  // Pagination
  const totalItems = filteredReservations.length;
  const totalPages = Math.ceil(totalItems / RESERVATIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * RESERVATIONS_PER_PAGE;
  const endIndex = Math.min(startIndex + RESERVATIONS_PER_PAGE, totalItems);
  const paginatedReservations = filteredReservations.slice(startIndex, endIndex);

  // Export CSV
  const exportCSV = () => {
    const csvRows = [];
    const headers = ["Post Title", "User Name", "Surname", "Contact", "Email", "Reserved At"];
    csvRows.push(headers.join(','));

    reservations.forEach(res => {
      csvRows.push([
        res.post_title,
        res.user_name,
        res.user_surname,
        res.user_contact,
        res.user_email,
        new Date(res.created_at).toLocaleString()
      ].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'reservations.csv';
    a.click();
  };

  // Export Excel
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reservations);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reservations");
    XLSX.writeFile(wb, "reservations.xlsx");
  };

  // Copy details
  const handleCopyDetails = () => {
    const text = JSON.stringify(selectedReservation, null, 2);
    navigator.clipboard.writeText(text);
    setToastOpen(true);
  };

  // Row click → open modal
  const openModal = (res) => {
    setSelectedReservation(res);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)',
        zIndex: 2000
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={6} />
          <Typography sx={{ color: '#fff', mt: 2 }}>Loading... ({loadingSeconds}s)</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div>
      <Header1 />

      <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: 2, minHeight: '70vh' }}>

        <Typography
          variant="h4"
          align="center"
          sx={{
            fontWeight: 'bold',
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 3
          }}
        >
          User Reservations
        </Typography>

        {/* SEARCH + EXPORTS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <TextField
            placeholder="Search by post title, user name, surname, or email..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            size="small"
            fullWidth
            sx={{ maxWidth: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
              sx: { backgroundColor: '#1e1e1e', color: '#fff', borderRadius: 1 }
            }}
          />

          <Box>
            <Button
              variant="contained"
              color="success"
              sx={{ mr: 1 }}
              startIcon={<DownloadIcon />}
              onClick={exportCSV}
            >
              CSV
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={exportExcel}
            >
              Excel
            </Button>
          </Box>
        </Box>

        {/* Summary Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#bbb', px: 1 }}>
          <Typography variant="body2">
            Showing <b>{startIndex + 1}</b>–<b>{endIndex}</b> of <b>{totalItems}</b> items
          </Typography>

          <Typography variant="body2">
            Page <b>{currentPage}</b> of <b>{totalPages}</b>
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* TABLE */}
        <TableContainer component={Paper} sx={{ maxHeight: '60vh', backgroundColor: '#1b1b1b' }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ bgcolor: '#222' }}>
              <TableRow>
                {[
                  { key: 'post_title', label: 'Post Title' },
                  { key: 'user_name', label: 'User Name' },
                  { key: 'user_surname', label: 'Surname' },
                  { key: 'user_contact', label: 'Contact' },
                  { key: 'user_email', label: 'Email' },
                  { key: 'created_at', label: 'Reserved At' },
                ].map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{ color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedReservations.map((res) => (
                <TableRow
                  key={res.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openModal(res)}
                >
                  <TableCell sx={{ color: '#ddd' }}>{res.post_title}</TableCell>
                  <TableCell sx={{ color: '#ddd' }}>{res.user_name}</TableCell>
                  <TableCell sx={{ color: '#ddd' }}>{res.user_surname}</TableCell>
                  <TableCell sx={{ color: '#ddd' }}>{res.user_contact}</TableCell>
                  <TableCell sx={{ color: '#ddd' }}>{res.user_email}</TableCell>
                  <TableCell sx={{ color: '#ddd' }}>
                    {new Date(res.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

          </Table>
        </TableContainer>

        {/* PAGINATION */}
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Box>

      <Footer2 />

      {/* DETAILS MODAL */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Reservation Details</Typography>
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Box>

          {selectedReservation && (
            <Box sx={{ mt: 2 }}>
              {Object.entries(selectedReservation).map(([key, value]) => (
                <Typography sx={{ mb: 1 }} key={key}>
                  <b>{key}:</b> {value?.toString()}
                </Typography>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleCopyDetails}
          >
            Copy All Details
          </Button>
        </Box>
      </Modal>

      {/* Toast */}
      <Snackbar open={toastOpen} autoHideDuration={2000} onClose={() => setToastOpen(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserReservationsTable;
